import React from 'react';
import {emotionStyled} from '@packages/utilities/styled';

import {
  Plugins,
  Expressions,
  TypeImplementation,
  getSystem,
  withSystem,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  type ContextualExpressionAnnotation,
  stripAll,
  updateAnn,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditor,
  updateExpressionAction,
} from '@packages/systems/ExpressionEditor-utils';
import {
  qualify,
  iconName,
  type IconName,
} from '@packages/systems/wfdl-Qualified';
import Button from '@packages/systems/design-system/Button';
import {RICH_TEXT_CONTENT_MODAL_OPENED} from '@packages/systems/designer/actions';
import {TElement, TList, Expression} from '@packages/systems/wfdl-ast';
import {
  useDesignerSelector,
  useDispatch,
  useFluxContext,
  type DesignerFlux,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {getNativeNodeCollectionData} from '@packages/systems/ContentEditor';
import {selectSelectedNativeIdPath} from '@packages/utilities/platform-selectors';
import {getContentEditorActive} from '@packages/shared/states';
import type {CollectionItem} from '@packages/systems/cms/types';
import type {FieldType} from '@packages/systems/dynamo/records/Field';

const selectRichTextContentModalElements = (state: DesignerStores) =>
  state.DesignerStore?.richTextContentModalElements ?? null;

/**
 * Return the CMS item + field the modal should save into, or null when the
 * parent Content Editor isn't editing a CMS-bound field whose selected node
 * matches the active item. Threaded through `richTextContentModalContext`
 * so the modal Content Editor can persist the override back to CMS.
 */
function getComponentRichTextModalCmsSaveTarget(
  state: DesignerStores
): {item: CollectionItem; field: FieldType} | null {
  const activeCE = getContentEditorActive(state.ContentEditorStore);
  if (!activeCE?.item || !activeCE?.field) {
    return null;
  }

  const nativeIdPath = selectSelectedNativeIdPath(state);
  if (!nativeIdPath?.length) {
    return null;
  }

  const {collectionItemId, isElementBoundToCMS} = getNativeNodeCollectionData({
    nativeIdPath,
  });
  if (!isElementBoundToCMS || !collectionItemId) {
    return null;
  }

  if (String(activeCE.item.get('_id')) !== collectionItemId) {
    return null;
  }

  return {item: activeCE.item, field: activeCE.field};
}

type SavedParentRichTextContext = {
  contentEditorState: NonNullable<ReturnType<typeof getContentEditorActive>>;
  componentInstanceId: string;
  expressionPath: ExpressionPath;
};

/**
 * Snapshot the parent Content Editor when the override modal opens so the
 * RICH_TEXT_CONTENT_MODAL_CLOSED reducer can splice the edited children back
 * into the component's prop at `expressionPath` and restore the parent
 * Content Editor. Returns null when there's no parent Content Editor,
 * selected component, or target prop path.
 */
function getSavedParentRichTextContext(
  state: DesignerStores,
  expr: ContextualExpression
): SavedParentRichTextContext | null {
  const parentCE = getContentEditorActive(state.ContentEditorStore);
  if (!parentCE) return null;

  const componentInstanceId =
    parentCE.selection?.type === 'Node' ? parentCE.selection.nodeId : null;
  const {expressionPath} = getAnn(expr).env;
  if (!componentInstanceId || !expressionPath.length) return null;

  return {
    contentEditorState: parentCE,
    componentInstanceId,
    expressionPath,
  };
}

const FullWidthButton = emotionStyled(Button)({
  width: '100%',
});

const richTextIcon = qualify<IconName>(
  Plugins.name('Basic'),
  iconName('RichText')
);

export const RichTextChildren = TypeImplementation.Alias({
  type: TList(TElement),
  system: {label: 'Content', displayName: 'Rich Text'},
  icon: TypeImplementation.IconByName(richTextIcon),
  render: function RichTextContentOverrideModal({expr, dispatch}) {
    const webflowDispatch = useDispatch();
    const flux = useFluxContext<DesignerFlux>();

    const richTextContentModalElements = useDesignerSelector(
      selectRichTextContentModalElements
    );
    const isModalOpen = Boolean(richTextContentModalElements);

    const openModal = () => {
      const state = flux.getState();
      const cmsSaveTarget = getComponentRichTextModalCmsSaveTarget(state);
      const parentCE = getSavedParentRichTextContext(state, expr);
      webflowDispatch({
        type: RICH_TEXT_CONTENT_MODAL_OPENED,
        payload: {
          richTextContentModalElements: stripAll(expr),
          onClose: (subtreeChildren: Expression) => {
            dispatch(updateExpressionAction(subtreeChildren));
          },
          richTextContentModalContext:
            cmsSaveTarget || parentCE ? {cmsSaveTarget, parentCE} : null,
        },
      });
    };

    // the custom settings works only if expr is a list of elements
    // if expr is not a list of elements, delegate to expression editor
    if (isListOfElements(expr)) {
      return (
        <FullWidthButton
          data-automation-id="RichTextType"
          disabled={isModalOpen}
          onClick={openModal}
          compact
        >
          Edit Rich Text
        </FullWidthButton>
      );
    }

    // we need to strip the top-level system label from expr annotation
    // to prevent duplicate rendering of labels by expression editor
    return (
      <ExpressionEditor
        expression={updateAnn(stripTopLevelSystemLabel, expr)}
        dispatch={dispatch}
      />
    );
  },
});

function isListOfElements(expr: ContextualExpression) {
  const strippedExpr = stripAll(expr);
  return Boolean(
    Expressions.getList(strippedExpr)?.every(Expressions.isElement)
  );
}

function stripTopLevelSystemLabel(ann: ContextualExpressionAnnotation) {
  const {type, env, errors} = ann;

  if (type) {
    const system = getSystem(type);
    if (system) {
      const {label, ...rest} = system;
      if (label) {
        return {type: withSystem(rest, type), env, errors};
      }
    }
  }

  return ann;
}
